"use client";

// The blog index: filtering, sorting and layout, over posts the server already
// fetched.
//
// WHY NOT useSearchParams. The obvious way to hold filter state in the URL is
// `useSearchParams`, and it silently costs this page its server-rendered HTML:
// on a statically rendered route, a Client Component that reads it is rendered
// only up to the nearest Suspense boundary on the server, so the static HTML
// gets the fallback and the article list exists only in the hydration payload.
// A marketing blog whose posts are not in its own markup is the wrong trade.
// Measured, not assumed — `bf-card` appeared zero times in .next/server/app/
// blog.html under that approach.
//
// So the query string is read through `useSyncExternalStore` instead, whose
// server snapshot is the empty string. The server therefore renders exactly
// the unfiltered, newest-first list — real markup, every post — and the client
// applies whatever the URL says on hydration. A filtered deep link shows the
// full list for one frame before narrowing, which is the entire cost.
//
// WHY history.replaceState AND NOT router.replace. Filtering never needs the
// server: the posts are already here. `replaceState` mutates the URL with no
// navigation and no RSC round trip, which is why the search box can write on
// every keystroke without a debounce and still feel instant. `replace`, not
// `push`, so twelve filter tweaks are not twelve Back presses.
//
// WHAT IS DELIBERATELY NOT HERE. No cover images: `cover_image_url` exists on
// the API and is null on every published post, so a card designed around one
// would render the same empty grey box this page was just rewritten to remove.
// No tag filter: categories already partition these posts, and two overlapping
// taxonomies in one toolbar is how a filter bar stops being read — tags stay
// reachable through the search box.

import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { safeHref } from "@/lib/safeUrl";

import type { Post } from "./_data/posts";

const HOUSE = "__house__";

type SortKey = "newest" | "oldest" | "az" | "za" | "short" | "long";

const SORTS: ReadonlyArray<readonly [SortKey, string]> = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["az", "Title A → Z"],
  ["za", "Title Z → A"],
  ["short", "Quick reads first"],
  ["long", "Longest reads first"],
];

/** Relative windows, in days. A visitor thinks "recently", not "since 6 March". */
const WHEN: ReadonlyArray<readonly [string, string, number | null]> = [
  ["", "Any time", null],
  ["30d", "Last 30 days", 30],
  ["90d", "Last 3 months", 90],
  ["365d", "Last year", 365],
];

/* ── The query string, as an external store ────────────────────────────────
   `getServerSnapshot` returns "" so the server renders the unfiltered list.
   `subscribe` covers both the Back button (popstate) and our own writes, which
   announce themselves because replaceState fires no event of its own. */
const URL_EVENT = "vio:blogfilters";

function subscribeToUrl(cb: () => void) {
  window.addEventListener("popstate", cb);
  window.addEventListener(URL_EVENT, cb);
  return () => {
    window.removeEventListener("popstate", cb);
    window.removeEventListener(URL_EVENT, cb);
  };
}

function useQueryString(): string {
  return useSyncExternalStore(
    subscribeToUrl,
    () => window.location.search,
    () => "",
  );
}

// `timeZone: "UTC"` because this function runs on both sides of hydration.
// `publishedAt` is a calendar date and `new Date("2026-09-05")` is UTC
// midnight, so the printed day would otherwise be the *renderer's* day: the
// build container's for the static HTML, the reader's for the hydrated
// render. West of Greenwich those disagree, which is a hydration mismatch on
// a global audience that the India site never sees (UTC+5:30 never rolls a
// UTC midnight backwards). blog/page.tsx pins UTC for the same reason, so the
// featured card and this one agree.
function formatDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  return new Date(iso).toLocaleDateString("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function authorKey(p: Post): string {
  return p.author.slug ?? HOUSE;
}

function IconList() {
  return (
    <svg aria-hidden="true" focusable="false" height="13" viewBox="0 0 16 16" width="13">
      <g fill="currentColor">
        <rect height="1.7" rx=".85" width="16" x="0" y="1.5" />
        <rect height="1.7" rx=".85" width="16" x="0" y="7.15" />
        <rect height="1.7" rx=".85" width="16" x="0" y="12.8" />
      </g>
    </svg>
  );
}

function IconGrid() {
  return (
    <svg aria-hidden="true" focusable="false" height="13" viewBox="0 0 16 16" width="13">
      <g fill="currentColor">
        <rect height="7" rx="1.6" width="7" x="0" y="0" />
        <rect height="7" rx="1.6" width="7" x="9" y="0" />
        <rect height="7" rx="1.6" width="7" x="0" y="9" />
        <rect height="7" rx="1.6" width="7" x="9" y="9" />
      </g>
    </svg>
  );
}

/* ── A popover that closes on Escape and on an outside click ─────────────── */
function usePopover() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        btnRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  return { open, setOpen, close, wrapRef, btnRef };
}

/* ── Multi-select with a type-ahead ─────────────────────────────────────── */
function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; label: string; hint?: string; count: number }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const { open, setOpen, close, wrapRef, btnRef } = usePopover();
  const [q, setQ] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(needle) ||
          (o.hint ?? "").toLowerCase().includes(needle),
      )
    : options;

  const toggle = (value: string) =>
    onChange(
      selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    );

  const summary =
    selected.length === 0
      ? label
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? label)
        : `${label} · ${selected.length}`;

  return (
    <div ref={wrapRef} className="bf-ms">
      <button
        ref={btnRef}
        aria-expanded={open}
        className={`bf-ctl bf-ms-btn${selected.length ? " on" : ""}`}
        type="button"
        onClick={() => {
          if (open) close();
          else {
            setQ("");
            setOpen(true);
          }
        }}
      >
        <span className="bf-ms-label">{summary}</span>
        <span aria-hidden="true" className="bf-caret">▾</span>
      </button>

      {open && (
        <div aria-label={label} className="bf-pop" role="group">
          <input
            ref={searchRef}
            className="bf-pop-q"
            placeholder={`Search ${label.toLowerCase()}…`}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="bf-pop-list">
            {shown.length === 0 ? (
              <p className="bf-pop-none">No match for “{q}”.</p>
            ) : (
              shown.map((o) => (
                <label key={o.value} className="bf-opt">
                  <input
                    checked={selected.includes(o.value)}
                    type="checkbox"
                    onChange={() => toggle(o.value)}
                  />
                  <span className="bf-opt-t">
                    {o.label}
                    {o.hint ? <em className="bf-opt-h">{o.hint}</em> : null}
                  </span>
                  <span className="bf-opt-n">{o.count}</span>
                </label>
              ))
            )}
          </div>
          {selected.length > 0 && (
            <button className="bf-pop-clear" type="button" onClick={() => onChange([])}>
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Relative date window ───────────────────────────────────────────────── */
function WhenSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { open, setOpen, close, wrapRef, btnRef } = usePopover();
  const current = WHEN.find(([v]) => v === value) ?? WHEN[0];

  return (
    <div ref={wrapRef} className="bf-ms">
      <button
        ref={btnRef}
        aria-expanded={open}
        className={`bf-ctl bf-ms-btn${value ? " on" : ""}`}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className="bf-ms-label">{current[1]}</span>
        <span aria-hidden="true" className="bf-caret">▾</span>
      </button>
      {open && (
        <div aria-label="Published" className="bf-pop bf-pop-sm" role="group">
          {WHEN.map(([v, l]) => (
            <button
              key={v || "any"}
              className={`bf-when${v === value ? " on" : ""}`}
              type="button"
              onClick={() => {
                onChange(v);
                close();
              }}
            >
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BlogIndexClient({ posts }: { posts: Post[] }) {
  const search = useQueryString();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const q = params.get("q") ?? "";
  const byRaw = params.get("by") ?? "";
  const catRaw = params.get("cat") ?? "";
  const authors = useMemo(() => byRaw.split(",").filter(Boolean), [byRaw]);
  const cats = useMemo(() => catRaw.split(",").filter(Boolean), [catRaw]);
  const when = params.get("when") ?? "";
  const sort = (params.get("sort") as SortKey) || "newest";
  const view = params.get("view") === "list" ? "list" : "grid";

  const setParam = useCallback(
    (patch: Record<string, string | string[] | null>) => {
      const next = new URLSearchParams(window.location.search);
      for (const [k, v] of Object.entries(patch)) {
        const value = Array.isArray(v) ? v.join(",") : v;
        if (!value) next.delete(k);
        else next.set(k, value);
      }
      const qs = next.toString();
      window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
      window.dispatchEvent(new Event(URL_EVENT));
    },
    [],
  );

  const clearAll = useCallback(() => setParam({ q: null, by: null, cat: null, when: null }), [setParam]);

  // "/" focuses search, the convention on every content site that has one.
  // Ignored while the visitor is already typing somewhere.
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (t?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Facets are counted over ALL posts, never the filtered set, so an option
  // does not silently read "0" because of a filter the reader is about to
  // change. Selecting one narrows the list; it does not rewrite the menu.
  const authorOptions = useMemo(() => {
    const m = new Map<string, { label: string; hint?: string; count: number }>();
    for (const p of posts) {
      const k = authorKey(p);
      const cur = m.get(k);
      if (cur) cur.count += 1;
      else
        m.set(k, {
          label: p.author.name,
          hint: p.author.headline ?? p.author.firmName ?? undefined,
          count: 1,
        });
    }
    return [...m.entries()]
      .map(([value, v]) => ({ value, ...v }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [posts]);

  const categoryOptions = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of posts) m.set(p.category, (m.get(p.category) ?? 0) + 1);
    return [...m.entries()]
      .map(([value, count]) => ({ value, label: value, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [posts]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // Resolved here rather than stored as a date, so a bookmarked "last 30
    // days" still means the last 30 days when it is opened next month.
    const days = WHEN.find(([v]) => v === when)?.[2] ?? null;
    let cutoff = "";
    if (days) {
      const d = new Date();
      d.setDate(d.getDate() - days);
      cutoff = d.toISOString().slice(0, 10);
    }

    const out = posts.filter((p) => {
      if (authors.length && !authors.includes(authorKey(p))) return false;
      if (cats.length && !cats.includes(p.category)) return false;
      // publishedAt is YYYY-MM-DD, so a string compare is the date compare —
      // no parsing, and no timezone to get wrong.
      if (cutoff && p.publishedAt < cutoff) return false;
      if (needle) {
        const hay = [p.title, p.description, p.author.name, p.category, ...(p.keywords ?? [])]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });

    const by: Record<SortKey, (a: Post, b: Post) => number> = {
      newest: (a, b) => b.publishedAt.localeCompare(a.publishedAt),
      oldest: (a, b) => a.publishedAt.localeCompare(b.publishedAt),
      az: (a, b) => a.title.localeCompare(b.title),
      za: (a, b) => b.title.localeCompare(a.title),
      short: (a, b) => a.readingMinutes - b.readingMinutes || b.publishedAt.localeCompare(a.publishedAt),
      long: (a, b) => b.readingMinutes - a.readingMinutes || b.publishedAt.localeCompare(a.publishedAt),
    };
    return [...out].sort(by[sort] ?? by.newest);
  }, [posts, q, authors, cats, when, sort]);

  // Every active filter as one removable chip. The controls already highlight,
  // but a chip row answers "what exactly am I looking at" in one line and, more
  // usefully, lets a filter be dropped without hunting for the control that set
  // it. Ordered as the toolbar is, so the two read as the same set.
  const chips: { key: string; label: string; onRemove: () => void }[] = [
    ...(q ? [{ key: "q", label: `“${q}”`, onRemove: () => setParam({ q: null }) }] : []),
    ...authors.map((a) => ({
      key: `by-${a}`,
      label: authorOptions.find((o) => o.value === a)?.label ?? a,
      onRemove: () => setParam({ by: authors.filter((v) => v !== a) }),
    })),
    ...cats.map((c) => ({
      key: `cat-${c}`,
      label: c,
      onRemove: () => setParam({ cat: cats.filter((v) => v !== c) }),
    })),
    ...(when
      ? [
          {
            key: "when",
            label: WHEN.find(([v]) => v === when)?.[1] ?? when,
            onRemove: () => setParam({ when: null }),
          },
        ]
      : []),
  ];

  // Nothing published at all — distinct from "nothing matches your filters".
  // This edition reaches it legitimately today: the corpus is empty, and the
  // data layer returns that as the real answer instead of substituting the
  // India product's bundled articles, which would be a section 1A violation. A filter toolbar above zero rows invites
  // the reader to narrow an empty set, so it is not rendered.
  if (posts.length === 0) {
    return (
      <section className="bf-results">
        <div className="bf-empty">
          <h2>No articles yet.</h2>
          <p>
            We are writing them. In the meantime, the product does not depend on
            the blog — try the scanner, or ask us what you were looking for.
          </p>
          <p style={{ marginTop: "14px" }}>
            <Link className="bf-clear bf-clear-lg" href="/scan">
              Scan a contract free →
            </Link>
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="bf-bar">
        <div className="bf-row">
          <div className="bf-search">
            <span aria-hidden="true" className="bf-search-i">⌕</span>
            <input
              ref={searchRef}
              aria-label="Search articles"
              className="bf-ctl bf-search-in"
              placeholder="Search articles, authors, tags…"
              type="search"
              value={q}
              onChange={(e) => setParam({ q: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Escape") setParam({ q: null });
              }}
            />
            <kbd aria-hidden="true" className="bf-kbd">/</kbd>
          </div>

          <MultiSelect
            label="Written by"
            options={authorOptions}
            selected={authors}
            onChange={(next) => setParam({ by: next })}
          />
          <MultiSelect
            label="Category"
            options={categoryOptions}
            selected={cats}
            onChange={(next) => setParam({ cat: next })}
          />
          <WhenSelect value={when} onChange={(v) => setParam({ when: v })} />

          <div className="bf-right">
            <label className="bf-sort">
              <span className="bf-sr">Sort by</span>
              <select className="bf-ctl" value={sort} onChange={(e) => setParam({ sort: e.target.value })}>
                {SORTS.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </label>

            <div aria-label="Layout" className="bf-view" role="group">
              <button
                aria-pressed={view === "list"}
                className={`bf-view-b${view === "list" ? " on" : ""}`}
                type="button"
                onClick={() => setParam({ view: "list" })}
              >
                <IconList /> List
              </button>
              <button
                aria-pressed={view === "grid"}
                className={`bf-view-b${view === "grid" ? " on" : ""}`}
                type="button"
                onClick={() => setParam({ view: null })}
              >
                <IconGrid /> Grid
              </button>
            </div>
          </div>
        </div>

        {/* One tap for the commonest intent. A dropdown is the right control
            for a long list; for five categories it is a click too many. */}
        {categoryOptions.length > 1 && (
          <div className="bf-pills">
            <button
              className={`bf-pill${cats.length === 0 ? " on" : ""}`}
              type="button"
              onClick={() => setParam({ cat: null })}
            >
              All
            </button>
            {categoryOptions.map((c) => (
              <button
                key={c.value}
                aria-pressed={cats.includes(c.value)}
                className={`bf-pill${cats.includes(c.value) ? " on" : ""}`}
                type="button"
                onClick={() =>
                  setParam({
                    cat: cats.includes(c.value)
                      ? cats.filter((v) => v !== c.value)
                      : [...cats, c.value],
                  })
                }
              >
                {c.label} <span className="bf-pill-n">{c.count}</span>
              </button>
            ))}
          </div>
        )}

        <div aria-live="polite" className="bf-meta" role="status">
          <span className="bf-count">
            {visible.length === posts.length
              ? `${posts.length} article${posts.length === 1 ? "" : "s"}`
              : `${visible.length} of ${posts.length} articles`}
          </span>
          {chips.length > 0 && (
            <>
              <span className="bf-chips">
                {chips.map((c) => (
                  <button key={c.key} className="bf-chip" type="button" onClick={c.onRemove}>
                    {c.label}
                    <span aria-hidden="true">×</span>
                    <span className="bf-sr"> — remove filter</span>
                  </button>
                ))}
              </span>
              <button className="bf-clear" type="button" onClick={clearAll}>
                Clear all
              </button>
            </>
          )}
        </div>
      </section>

      <section className="bf-results">
        {visible.length === 0 ? (
          <div className="bf-empty">
            <h2>Nothing matches those filters.</h2>
            <p>
              {q ? <>No article mentions “{q}”. </> : null}
              Try a wider date range, or clear the filters and start again.
            </p>
            <button className="bf-clear bf-clear-lg" type="button" onClick={clearAll}>
              Clear all filters
            </button>
          </div>
        ) : (
          <div className={view === "list" ? "bf-list" : "bf-grid"}>
            {visible.map((p) => {
              // The byline avatar is author-supplied (PUT /blog/me/profile) and
              // rendered on our own domain, so it goes through safeHref, which
              // rejects the `//host` form the backend's URL check admits.
              const avatar = safeHref(p.author.avatarUrl);
              return (
                <a key={p.slug} className="bf-card" href={`/blog/${p.slug}`}>
                  <div className="bf-card-top">
                    <span className="bf-cat">{p.category}</span>
                    <span className="bf-min">{p.readingMinutes} min</span>
                  </div>
                  <h2 className="bf-card-t">{p.title}</h2>
                  <p className="bf-card-d">{p.description}</p>
                  <div className="bf-card-by">
                    {avatar ? (
                      // Remote URL from the blog image store; next/image would
                      // need that host in next.config remotePatterns.
                      // eslint-disable-next-line @next/next/no-img-element
                      <img alt="" className="bf-av" height={22} loading="lazy" src={avatar} width={22} />
                    ) : null}
                    <span className="bf-by-t">
                      <b>{p.author.name}</b>
                      {p.author.role && p.author.role !== p.author.name ? <>, {p.author.role}</> : null}
                    </span>
                  </div>
                  <div className="bf-card-m">
                    <time dateTime={p.publishedAt}>{formatDate(p.publishedAt)}</time>
                    <span aria-hidden="true">·</span>
                    <span className="bf-go">Read →</span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>

      <p className="bf-foot">
        Looking for something specific? <Link href="/contact">Ask us</Link>.
      </p>
    </>
  );
}
